import { ref } from "vue";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import type { SizingsManifoldMutationVariables } from "gql/api";
import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";
import useForm from "@/composables/forms/useForm";
import useDestructureScenario from "@/composables/useDestructureScenario";
import useCreateRevisionFromQuery from "@/composables/useCreateRevisionFromQuery";
import transplantJournal from "@/utils/transplantJournal";
import { jsonToPostgresArray } from "@/utils/utils-dependent";
import { applySizingRecommendation, type RecommendationForms } from "./applySizingRecommendation";
import { recommendationProvenance, equipmentSelectionSignature } from "./recommendationProvenance";

/** Build one independent sizing and one recommended scenario. All editable
 * journals (including well/operating inputs) get new IDs through the existing
 * sizing clone mechanism. Catalog IDs and customer/well identity are retained.
 */
export function buildRecommendedSizing(
  sourceSizing: Sizing,
  sourceScenario: Scenario,
  option: AIEquipmentSuggestionOption
): { sizingId: string; scenarioId: string; variables: SizingsManifoldMutationVariables } {
  if (!sourceScenario.equipment || !sourceScenario.parameters ||
      !sourceScenario.well_properties || !sourceScenario.correlations) {
    throw new Error("The source scenario is incomplete. Reload it before creating a sizing.");
  }
  if (sourceScenario.sizing_id !== sourceSizing.id) {
    throw new Error("The source scenario does not belong to this sizing.");
  }
  const priority = { performance: "Performance", runlife: "Run Life", cost: "Cost" }[option.priority];
  const sizing = transplantJournal({
    ...sourceSizing,
    title: `${sourceSizing.title || "Sizing"} — AI ${priority}`,
    disposition: "unknown",
    installed_scenario_id: null,
    imported_sizing_id: null,
    is_shared: false,
    share_with_customer: false,
    scenario_order: [sourceScenario.id],
    scenarios: [{ ...sourceScenario, r_performance_evaluations: [] }]
  })!;
  const scenario = sizing.scenarios![0];
  scenario.title = `AI ${priority}`;
  const source = useDestructureScenario(scenario);
  const forms: RecommendationForms = {
    parameters: useForm(source.parameters),
    equipment: useForm(source.equipment),
    pumps: useForm(ref({})),
    housings: useForm(ref({})),
    intakes: useForm(ref({})),
    seals: useForm(ref({})),
    cables: useForm(ref({}))
  };
  applySizingRecommendation(option, forms);
  scenario.equipment = forms.equipment.data.value;
  scenario.parameters = forms.parameters!.data.value;
  scenario.ai_recommendation = {
    ...recommendationProvenance(option),
    appliedEquipmentSignature: equipmentSelectionSignature(scenario.equipment),
    sourceSizingId: sourceSizing.id,
    sourceScenarioId: sourceScenario.id
  };

  const revision = (data: unknown, omitted: string[] = []) =>
    useCreateRevisionFromQuery(data as Record<string, unknown>, omitted);
  const withOrders = (data: Record<string, unknown>) => {
    for (const key of Object.keys(data)) {
      if ((key.endsWith("_order") || key === "entry_rows") && Array.isArray(data[key])) {
        data[key] = jsonToPostgresArray(data[key] as string[]);
      }
    }
    return data;
  };
  const survey = source.deviationSurvey.value;
  const variables = {
    sizings_revisions: [withOrders(revision(sizing, [
      "parent_sizing", "author", "well", "customer", "scenarios", "imported_from_sizing"
    ]))],
    scenarios_revisions: [revision(scenario, [
      "author", "equipment", "parameters", "correlations", "well_properties"
    ])],
    scenario_equipment_revisions: [withOrders(forms.equipment.prepare())],
    scenario_parameters_revisions: [forms.parameters!.prepare()],
    scenario_well_properties_revisions: [revision(source.wellProperties.value)],
    scenario_correlations_revisions: [revision(source.correlations.value)],
    deviation_survey: survey?.id ? [withOrders(revision(survey))] : [],
    scenario_equipment_xref_pumps_revisions: forms.pumps.prepareAllAsArray().map(withOrders),
    scenario_equipment_xref_housings_revisions: forms.housings.prepareAllAsArray(),
    scenario_equipment_xref_intakes_revisions: forms.intakes.prepareAllAsArray(),
    scenario_equipment_xref_seals_revisions: forms.seals.prepareAllAsArray(),
    scenario_equipment_xref_cables_revisions: forms.cables.prepareAllAsArray(),
    sc_equip_xref_uphole_ytool_bypass_tubings_revisions: [],
    sc_equip_xref_uphole_ytool_equip_tubings_revisions: [],
    sc_equip_xref_downhole_ytool_bypass_tubings_revisions: [],
    sc_equip_xref_downhole_ytool_equip_tubings_revisions: []
  } as SizingsManifoldMutationVariables;
  return { sizingId: sizing.id, scenarioId: scenario.id, variables };
}
